import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useAuthQuery } from './use-auth-query';
import { useBatchedPaginatedQuery } from './use-batched-paginated-query';
import type { BatchFetcherOpts } from './use-batched-paginated-query';

export function useUserSearch(role: string, search: string) {
  return useAuthQuery({
    queryKey: queryKeys.users.search(role, search),
    queryFn: () => api.searchUsers(role, search),
    enabled: search.length >= 3,
    staleTime: 1000 * 60 * 5,
  });
}

export function useAllUserSearch(search: string) {
  return useAuthQuery({
    queryKey: queryKeys.users.searchAll(search),
    queryFn: () => api.searchAllUsers(search),
    enabled: search.length >= 3,
    staleTime: 1000 * 60 * 5,
  });
}

export function useUserPropertyCount(userId: string | null) {
  return useAuthQuery({
    queryKey: queryKeys.users.propertyCount(userId ?? ''),
    queryFn: () => api.getUserPropertyCount(userId!),
    enabled: !!userId,
    staleTime: 1000 * 60 * 2,
  });
}

export function useUnassignedLandMonitoringTasks(
  opts: {
    viewPage: number;
    viewPageSize?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    search?: string;
  } = { viewPage: 1 },
) {
  const viewPageSize = opts.viewPageSize ?? 10;
  return useBatchedPaginatedQuery(
    queryKeys.tasks.all(),
    (fetchOpts: BatchFetcherOpts) =>
      api.getTasks({
        page: fetchOpts.page,
        limit: fetchOpts.limit,
        sortBy: fetchOpts.sortBy as string | undefined,
        sortOrder: fetchOpts.sortOrder as 'asc' | 'desc' | undefined,
        status: 'AVAILABLE',
        type: 'LAND_MONITORING',
        search: fetchOpts.search as string | undefined,
      }),
    {
      viewPage: opts.viewPage,
      viewPageSize,
      sortBy: opts.sortBy,
      sortOrder: opts.sortOrder,
      filters: { status: 'AVAILABLE', type: 'LAND_MONITORING', search: opts.search },
      staleTime: 1000 * 60,
    },
  );
}
